import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, PencilSimple } from "@phosphor-icons/react";
import { TransferToSacButton } from "@/components/TransferToSacDialog";
import { LinkParentButton } from "@/components/LinkParentDialog";

const STATUS_CLASS = {
  active: "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10",
  draft: "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10",
};
const DEFAULT_STATUS_CLASS = "border-[var(--vs-border)]";

export default function Students() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState("");

  const refresh = () => api.get("/students").then((r) => setStudents(r.data)).catch(() => setStudents([]));
  useEffect(() => { refresh(); }, []);

  const visible = students.filter(
    (s) =>
      !filter ||
      s.name?.toLowerCase().includes(filter.toLowerCase()) ||
      s.vs_id?.toLowerCase().includes(filter.toLowerCase())
  );

  const showAadhar = user?.role !== "donor";

  return (
    <DashboardLayout title="Students" subtitle="Encrypted PII at rest. Visibility scoped to your role.">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative max-w-xs w-full">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vs-text-secondary)]" />
          <Input
            data-testid="students-filter-input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name or VS-ID"
            className="pl-9 bg-white"
          />
        </div>
        <div className="text-xs font-mono text-[var(--vs-text-secondary)]">{visible.length} students</div>
      </div>

      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">VS-ID</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Class</th>
              <th className="px-4 py-3">Guardian</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Aadhar</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => (
              <tr key={s.id} data-testid={`student-row-${s.vs_id}`} className="border-t border-[var(--vs-border)] hover:bg-[var(--vs-bg-subtle)]/50">
                <td className="px-4 py-3 font-mono text-xs">{s.vs_id}</td>
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3">{s.class_name ? `${s.class_name}-${s.section || ""}` : "—"}</td>
                <td className="px-4 py-3">{s.guardian_name || s.parent_father_name || s.parent_mother_name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.phone || s.parent_phone || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {showAadhar ? (s.aadhar || "—") : (s.aadhar || "—")}
                </td>
                <td className="px-4 py-3">
                  <Badge
                    variant="outline"
                    className={STATUS_CLASS[s.status] || DEFAULT_STATUS_CLASS}
                  >
                    {s.status}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {user?.role !== "donor" && user?.role !== "teacher" && (
                      <Link
                        to={`/app/students/${s.id}`}
                        data-testid={`manage-student-${s.vs_id}`}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[var(--vs-border)] hover:bg-[var(--vs-bg-subtle)]"
                      >
                        <PencilSimple size={12} /> Manage
                      </Link>
                    )}
                    {(user?.role === "hostel_admin" || user?.role === "school_admin" || user?.role === "super_admin") && s.sac_status !== "active" && (
                      <TransferToSacButton student={s} onSuccess={refresh} />
                    )}
                    {(user?.role === "hostel_admin" || user?.role === "school_admin" || user?.role === "super_admin") && (
                      <LinkParentButton student={s} onLinked={refresh} />
                    )}
                    {s.sac_status === "active" && (
                      <span className="text-[10px] uppercase tracking-widest font-mono text-[var(--vs-secondary)] flex items-center gap-1" data-testid={`sac-active-tag-${s.id}`}>
                        SAC active
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--vs-text-secondary)]">No students match your filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
